import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FileText, Briefcase, Palmtree, ClipboardList, ChevronRight,
  MapPin, Building2, DollarSign, Globe, CheckCircle, XCircle,
  Clock, ExternalLink, Loader2, X, Plus, Info, Users, Scale,
  Phone, Mail, Award, Star, Languages
} from 'lucide-react';
import { Button, Badge } from '@/components/common';
import SEO from '@/components/common/SEO';
import { VISA_DATA, getDigitalNomadCountries, VISA_TYPES, type CountryVisaInfo } from '@/data/visaData';
import {
  useVisaSponsoringJobs, useSubmitJob, type JobSubmissionInput,
  useAttorneys, useSubmitAttorney, type AttorneySubmissionInput
} from '@/hooks/useImmigration';

// Tab definitions
const TABS = [
  { id: 'guide', name: 'Visa Guide', icon: FileText },
  { id: 'work', name: 'Work Visas', icon: Briefcase },
  { id: 'nomad', name: 'Digital Nomad', icon: Palmtree },
  { id: 'jobs', name: 'Visa Jobs', icon: ClipboardList },
  { id: 'attorneys', name: 'Attorneys', icon: Scale },
] as const;

type TabId = typeof TABS[number]['id'];

// Attorney specialties
const ATTORNEY_SPECIALTIES = [
  'H-1B Visa',
  'L-1 Visa',
  'E-2 Investor Visa',
  'O-1 Visa',
  'Green Card / EB-2/EB-3',
  'Family Immigration',
  'Asylum / Refugee',
  'Deportation Defense',
  'Business Immigration',
  'Student Visas (F-1)',
  'Work Permits',
  'Citizenship / Naturalization',
];

// Core 8 countries
const COUNTRIES = [
  { code: 'ALL', name: 'All Countries', flag: '🌍' },
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'ID', name: 'Indonesia', flag: '🇮🇩' },
  { code: 'CN', name: 'China', flag: '🇨🇳' },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬' },
];

export default function Immigration() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabId>((searchParams.get('tab') as TabId) || 'guide');
  const [selectedCountry, setSelectedCountry] = useState(searchParams.get('country') || 'ALL');
  const [showJobModal, setShowJobModal] = useState(false);
  const [showAttorneyModal, setShowAttorneyModal] = useState(false);
  const [expandedCountry, setExpandedCountry] = useState<string | null>(null);

  // Handle tab change
  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);
    setSearchParams({ tab, country: selectedCountry });
  };

  // Handle country change
  const handleCountryChange = (code: string) => {
    setSelectedCountry(code);
    setSearchParams({ tab: activeTab, country: code });
  };

  // Filter visa data by selected country
  const filteredVisaData = useMemo(() => {
    if (selectedCountry === 'ALL') return VISA_DATA;
    return VISA_DATA.filter(v => v.code === selectedCountry);
  }, [selectedCountry]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <SEO
        title="Korean Immigration & Visa Guides"
        description="Comprehensive visa and immigration guides for Koreans moving abroad. Country-specific requirements, timelines, and tips."
        path="/immigration"
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="w-8 h-8" />
            <h1 className="text-2xl sm:text-3xl font-bold">Immigration Help</h1>
          </div>
          <p className="text-blue-100 text-sm sm:text-base">
            Visa guides, work permits, and visa-sponsoring job opportunities for Koreans abroad
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{tab.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Country Filter */}
      <div className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex overflow-x-auto scrollbar-hide gap-2 -mx-4 px-4 sm:mx-0 sm:px-0">
            {COUNTRIES.map((country) => (
              <button
                key={country.code}
                onClick={() => handleCountryChange(country.code)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                  selectedCountry === country.code
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
                }`}
              >
                <span>{country.flag}</span>
                <span>{country.code === 'ALL' ? 'All' : country.code}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'guide' && (
          <VisaGuideTab
            visaData={filteredVisaData}
            expandedCountry={expandedCountry}
            setExpandedCountry={setExpandedCountry}
          />
        )}
        {activeTab === 'work' && (
          <WorkVisasTab visaData={filteredVisaData} />
        )}
        {activeTab === 'nomad' && (
          <DigitalNomadTab />
        )}
        {activeTab === 'jobs' && (
          <VisaJobsTab
            selectedCountry={selectedCountry}
            onSubmitJob={() => setShowJobModal(true)}
          />
        )}
        {activeTab === 'attorneys' && (
          <AttorneysTab
            selectedCountry={selectedCountry}
            onSubmitAttorney={() => setShowAttorneyModal(true)}
          />
        )}
      </div>

      {/* Submit Job Modal */}
      {showJobModal && (
        <SubmitJobModal onClose={() => setShowJobModal(false)} />
      )}

      {/* Submit Attorney Modal */}
      {showAttorneyModal && (
        <SubmitAttorneyModal onClose={() => setShowAttorneyModal(false)} />
      )}
    </div>
  );
}

// Visa Guide Tab Component
function VisaGuideTab({
  visaData,
  expandedCountry,
  setExpandedCountry,
}: {
  visaData: CountryVisaInfo[];
  expandedCountry: string | null;
  setExpandedCountry: (code: string | null) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <p className="font-medium mb-1">Important Notice</p>
            <p>This information is for general guidance only. Always verify requirements with official government sources or consult an immigration attorney.</p>
          </div>
        </div>
      </div>

      {visaData.map((country) => (
        <div
          key={country.code}
          className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden"
        >
          {/* Country Header */}
          <button
            onClick={() => setExpandedCountry(expandedCountry === country.code ? null : country.code)}
            className="w-full px-4 sm:px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">{country.flag}</span>
              <div className="text-left">
                <h3 className="font-semibold text-gray-900 dark:text-white">{country.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{country.nameKo}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <span>Tourist: {country.tourist.duration}</span>
                <span className="text-gray-300 dark:text-gray-600">|</span>
                <span>Work Visas: {country.workVisas.length}</span>
                {country.digitalNomad.available && (
                  <>
                    <span className="text-gray-300 dark:text-gray-600">|</span>
                    <Badge variant="green" size="sm">Digital Nomad</Badge>
                  </>
                )}
              </div>
              <ChevronRight
                className={`w-5 h-5 text-gray-400 transition-transform ${
                  expandedCountry === country.code ? 'rotate-90' : ''
                }`}
              />
            </div>
          </button>

          {/* Expanded Content */}
          {expandedCountry === country.code && (
            <div className="px-4 sm:px-6 pb-6 border-t border-gray-100 dark:border-slate-700">
              {/* Tourist Visa */}
              <div className="py-4 border-b border-gray-100 dark:border-slate-700">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-blue-500" />
                  Tourist / Visitor
                </h4>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      <span className="font-medium">{country.tourist.name}</span>
                      <span className="text-gray-400 mx-2">-</span>
                      {country.tourist.duration}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {country.tourist.visaRequired ? '⚠️ Visa required' : '✅ Visa-free for Korean citizens'}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      <span className="font-medium">For Korean Citizens:</span>
                      <br />
                      {country.tourist.koreanCitizenNote}
                    </p>
                  </div>
                </div>
              </div>

              {/* Work Visas Summary */}
              <div className="py-4 border-b border-gray-100 dark:border-slate-700">
                <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-green-500" />
                  Work Visas ({country.workVisas.length} options)
                </h4>
                <div className="grid sm:grid-cols-2 gap-2">
                  {country.workVisas.slice(0, 4).map((visa, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between bg-gray-50 dark:bg-slate-700/50 rounded-lg px-3 py-2"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {visa.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {visa.duration}
                        </p>
                      </div>
                      {visa.sponsorshipRequired ? (
                        <Badge variant="amber" size="sm">Sponsor Req.</Badge>
                      ) : (
                        <Badge variant="green" size="sm">Self-Apply</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Digital Nomad */}
              <div className="py-4 border-b border-gray-100 dark:border-slate-700">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <Palmtree className="w-4 h-4 text-amber-500" />
                  Digital Nomad / Remote Work
                </h4>
                {country.digitalNomad.available ? (
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="font-medium text-green-800 dark:text-green-200">
                        {country.digitalNomad.name} Available
                      </span>
                    </div>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      Duration: {country.digitalNomad.duration}
                      {country.digitalNomad.incomeRequirement && (
                        <> | Income: {country.digitalNomad.incomeRequirement}</>
                      )}
                    </p>
                  </div>
                ) : (
                  <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <XCircle className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-600 dark:text-gray-300">
                        No dedicated digital nomad visa
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {country.digitalNomad.notes}
                    </p>
                  </div>
                )}
              </div>

              {/* Useful Links */}
              <div className="pt-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Official Resources</h4>
                <div className="flex flex-wrap gap-2">
                  {country.usefulLinks.map((link, idx) => (
                    <a
                      key={idx}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg text-sm hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      {link.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Work Visas Tab Component
function WorkVisasTab({ visaData }: { visaData: CountryVisaInfo[] }) {
  const [selectedVisa, setSelectedVisa] = useState<{country: CountryVisaInfo, visa: typeof visaData[0]['workVisas'][0]} | null>(null);

  return (
    <div className="space-y-6">
      {visaData.map((country) => (
        <div key={country.code}>
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white mb-4">
            <span className="text-xl">{country.flag}</span>
            {country.name} Work Visas
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {country.workVisas.map((visa, idx) => (
              <button
                key={idx}
                onClick={() => setSelectedVisa({ country, visa })}
                className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700 text-left hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {visa.name}
                  </h4>
                  {visa.sponsorshipRequired ? (
                    <Badge variant="amber" size="sm">Sponsor</Badge>
                  ) : (
                    <Badge variant="green" size="sm">Self</Badge>
                  )}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-3">
                  {visa.description}
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <Clock className="w-3.5 h-3.5" />
                  <span>{visa.duration}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}

      {/* Visa Detail Modal */}
      {selectedVisa && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-slate-800 px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xl">{selectedVisa.country.flag}</span>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {selectedVisa.visa.name}
                </h3>
              </div>
              <button
                onClick={() => setSelectedVisa(null)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-gray-600 dark:text-gray-300">{selectedVisa.visa.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Duration</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedVisa.visa.duration}</p>
                </div>
                {selectedVisa.visa.processingTime && (
                  <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Processing</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedVisa.visa.processingTime}</p>
                  </div>
                )}
                {selectedVisa.visa.cost && (
                  <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Cost</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedVisa.visa.cost}</p>
                  </div>
                )}
                <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Sponsorship</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedVisa.visa.sponsorshipRequired ? 'Required' : 'Not required'}
                  </p>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Requirements</h4>
                <ul className="space-y-1.5">
                  {selectedVisa.visa.requirements.map((req, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Digital Nomad Tab Component
function DigitalNomadTab() {
  const nomadCountries = getDigitalNomadCountries();
  const otherCountries = VISA_DATA.filter(v => !v.digitalNomad.available);

  return (
    <div className="space-y-6">
      {/* Countries with Digital Nomad Visas */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-500" />
          Countries with Digital Nomad Visas
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {nomadCountries.map((country) => (
            <div
              key={country.code}
              className="bg-white dark:bg-slate-800 rounded-xl p-5 border-2 border-green-200 dark:border-green-800"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl">{country.flag}</span>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">{country.name}</h4>
                  <Badge variant="green">{country.digitalNomad.name}</Badge>
                </div>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-300">
                    Duration: {country.digitalNomad.duration}
                  </span>
                </div>
                {country.digitalNomad.incomeRequirement && (
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300">
                      Income: {country.digitalNomad.incomeRequirement}
                    </span>
                  </div>
                )}
                {country.digitalNomad.cost && (
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300">
                      Cost: {country.digitalNomad.cost}
                    </span>
                  </div>
                )}
                {country.digitalNomad.notes && (
                  <p className="text-gray-500 dark:text-gray-400 mt-2">
                    {country.digitalNomad.notes}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Countries without Digital Nomad Visas */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <XCircle className="w-5 h-5 text-gray-400" />
          No Dedicated Digital Nomad Visa
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {otherCountries.map((country) => (
            <div
              key={country.code}
              className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700"
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl">{country.flag}</span>
                <h4 className="font-medium text-gray-900 dark:text-white">{country.name}</h4>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {country.digitalNomad.notes}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Visa Jobs Tab Component
function VisaJobsTab({
  selectedCountry,
  onSubmitJob,
}: {
  selectedCountry: string;
  onSubmitJob: () => void;
}) {
  const [koreanPreferred, setKoreanPreferred] = useState(false);
  const [remoteOnly, setRemoteOnly] = useState(false);

  const { data, isLoading } = useVisaSponsoringJobs({
    country: selectedCountry === 'ALL' ? undefined : selectedCountry,
    koreanPreferred,
    remote: remoteOnly,
    limit: 20,
  });

  const jobs = data?.jobs || [];

  return (
    <div className="space-y-6">
      {/* Pricing notice */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <Briefcase className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900 dark:text-blue-100">Visa-Sponsoring Job Board</h3>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              Post jobs that sponsor work visas for Korean professionals.
              Listings start at <span className="font-semibold">$99/month</span>.
            </p>
          </div>
        </div>
      </div>

      {/* Header with filters and submit button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setKoreanPreferred(!koreanPreferred)}
            className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
              koreanPreferred
                ? 'bg-coral-500 text-white'
                : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300'
            }`}
          >
            🇰🇷 Korean Preferred
          </button>
          <button
            onClick={() => setRemoteOnly(!remoteOnly)}
            className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
              remoteOnly
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300'
            }`}
          >
            🏠 Remote OK
          </button>
        </div>
        <Button onClick={onSubmitJob} variant="coral" size="sm">
          <Plus className="w-4 h-4 mr-1" />
          Post a Job
        </Button>
      </div>

      {/* Job listings */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : jobs.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl p-8 text-center">
          <ClipboardList className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No job listings yet
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Be the first to post a visa-sponsoring job for Korean professionals!
          </p>
          <Button onClick={onSubmitJob} variant="coral">
            <Plus className="w-4 h-4 mr-1" />
            Post a Job
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {jobs.map((job) => {
            const visaTypes = job.visa_types_json ? JSON.parse(job.visa_types_json) : [];
            const countryData = COUNTRIES.find(c => c.code === job.country_code);

            return (
              <div
                key={job.id}
                className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
              >
                <div className="flex items-start gap-3 mb-3">
                  {job.company_logo_url ? (
                    <img
                      src={job.company_logo_url}
                      alt={job.company_name}
                      className="w-10 h-10 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gray-100 dark:bg-slate-700 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 dark:text-white truncate">
                      {job.job_title}
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                      {job.company_name}
                    </p>
                  </div>
                </div>

                <div className="space-y-2 mb-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="truncate">
                      {countryData?.flag} {job.location}
                    </span>
                    {job.is_remote === 1 && (
                      <Badge variant="blue" size="sm">Remote OK</Badge>
                    )}
                  </div>
                  {(job.salary_min || job.salary_max) && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <DollarSign className="w-4 h-4 text-gray-400" />
                      <span>
                        {job.salary_min && job.salary_max
                          ? `${job.salary_currency} ${job.salary_min.toLocaleString()} - ${job.salary_max.toLocaleString()}`
                          : job.salary_min
                          ? `From ${job.salary_currency} ${job.salary_min.toLocaleString()}`
                          : `Up to ${job.salary_currency} ${job.salary_max?.toLocaleString()}`}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-1 mb-3">
                  {visaTypes.slice(0, 3).map((visa: string, idx: number) => (
                    <Badge key={idx} variant="gray" size="sm">{visa}</Badge>
                  ))}
                  {(job.korean_required || job.korean_preferred) && (
                    <Badge variant="coral" size="sm">
                      🇰🇷 {job.korean_required ? 'Required' : 'Preferred'}
                    </Badge>
                  )}
                </div>

                {job.apply_url && (
                  <a
                    href={job.apply_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Apply Now
                    <ExternalLink className="w-3.5 h-3.5 inline-block ml-1" />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Submit Job Modal Component
function SubmitJobModal({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<Partial<JobSubmissionInput>>({
    job_type: 'full-time',
    salary_currency: 'USD',
    visa_types: [],
  });
  const [error, setError] = useState('');

  const submitJob = useSubmitJob();

  const handleSubmit = async () => {
    setError('');

    if (!formData.company_name || !formData.job_title || !formData.job_description ||
        !formData.location || !formData.country_code || !formData.contact_email) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      await submitJob.mutateAsync(formData as JobSubmissionInput);
      onClose();
      alert('Thank you! Your job listing has been submitted for review.');
    } catch (err: any) {
      setError(err.message || 'Failed to submit job listing');
    }
  };

  const updateForm = (field: keyof JobSubmissionInput, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleVisaType = (type: string) => {
    const current = formData.visa_types || [];
    if (current.includes(type)) {
      updateForm('visa_types', current.filter(t => t !== type));
    } else {
      updateForm('visa_types', [...current, type]);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-slate-800 px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">Post a Visa-Sponsoring Job</h3>
            <p className="text-sm text-gray-500">Step {step} of 3</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress */}
        <div className="px-6 py-3 bg-gray-50 dark:bg-slate-700/50">
          <div className="flex gap-2">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`flex-1 h-1.5 rounded-full ${
                  s <= step ? 'bg-blue-600' : 'bg-gray-200 dark:bg-slate-600'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg p-3 text-sm">
              {error}
            </div>
          )}

          {step === 1 && (
            <>
              <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-500" />
                Company Information
              </h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={formData.company_name || ''}
                  onChange={(e) => updateForm('company_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                  placeholder="Acme Corporation"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Company Website
                </label>
                <input
                  type="url"
                  value={formData.company_website || ''}
                  onChange={(e) => updateForm('company_website', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                  placeholder="https://example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Company Logo URL
                </label>
                <input
                  type="url"
                  value={formData.company_logo_url || ''}
                  onChange={(e) => updateForm('company_logo_url', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                  placeholder="https://example.com/logo.png"
                />
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-green-500" />
                Job Details
              </h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Job Title *
                </label>
                <input
                  type="text"
                  value={formData.job_title || ''}
                  onChange={(e) => updateForm('job_title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                  placeholder="Senior Software Engineer"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Job Description *
                </label>
                <textarea
                  value={formData.job_description || ''}
                  onChange={(e) => updateForm('job_description', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                  placeholder="Describe the role, responsibilities, and requirements..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Location *
                  </label>
                  <input
                    type="text"
                    value={formData.location || ''}
                    onChange={(e) => updateForm('location', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                    placeholder="San Francisco, CA"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Country *
                  </label>
                  <select
                    value={formData.country_code || ''}
                    onChange={(e) => updateForm('country_code', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select country</option>
                    {COUNTRIES.filter(c => c.code !== 'ALL').map((c) => (
                      <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Job Type
                  </label>
                  <select
                    value={formData.job_type || 'full-time'}
                    onChange={(e) => updateForm('job_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                  >
                    <option value="full-time">Full-time</option>
                    <option value="part-time">Part-time</option>
                    <option value="contract">Contract</option>
                    <option value="internship">Internship</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_remote || false}
                      onChange={(e) => updateForm('is_remote', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Remote OK</span>
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Salary Min
                  </label>
                  <input
                    type="number"
                    value={formData.salary_min || ''}
                    onChange={(e) => updateForm('salary_min', parseInt(e.target.value) || undefined)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                    placeholder="80000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Salary Max
                  </label>
                  <input
                    type="number"
                    value={formData.salary_max || ''}
                    onChange={(e) => updateForm('salary_max', parseInt(e.target.value) || undefined)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                    placeholder="150000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Currency
                  </label>
                  <select
                    value={formData.salary_currency || 'USD'}
                    onChange={(e) => updateForm('salary_currency', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                  >
                    <option value="USD">USD</option>
                    <option value="CAD">CAD</option>
                    <option value="GBP">GBP</option>
                    <option value="AUD">AUD</option>
                    <option value="SGD">SGD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-500" />
                Visa & Contact Information
              </h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Visa Types Sponsored (select all that apply)
                </label>
                <div className="flex flex-wrap gap-2">
                  {VISA_TYPES.filter(v =>
                    v.country === 'ALL' ||
                    v.country === formData.country_code
                  ).map((visa) => (
                    <button
                      key={visa.id}
                      type="button"
                      onClick={() => toggleVisaType(visa.name)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        formData.visa_types?.includes(visa.name)
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {visa.name}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Korean Language
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.korean_required || false}
                      onChange={(e) => {
                        updateForm('korean_required', e.target.checked);
                        if (e.target.checked) updateForm('korean_preferred', false);
                      }}
                      className="w-4 h-4 text-coral-500 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Required</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.korean_preferred || false}
                      onChange={(e) => {
                        updateForm('korean_preferred', e.target.checked);
                        if (e.target.checked) updateForm('korean_required', false);
                      }}
                      className="w-4 h-4 text-coral-500 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Preferred</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Apply URL
                </label>
                <input
                  type="url"
                  value={formData.apply_url || ''}
                  onChange={(e) => updateForm('apply_url', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                  placeholder="https://company.com/careers/apply"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Contact Name
                  </label>
                  <input
                    type="text"
                    value={formData.contact_name || ''}
                    onChange={(e) => updateForm('contact_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                    placeholder="Jane Smith"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Contact Email *
                  </label>
                  <input
                    type="email"
                    value={formData.contact_email || ''}
                    onChange={(e) => updateForm('contact_email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                    placeholder="hr@company.com"
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white dark:bg-slate-800 px-6 py-4 border-t border-gray-200 dark:border-slate-700 flex justify-between">
          <Button
            variant="outline"
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
          >
            {step > 1 ? 'Back' : 'Cancel'}
          </Button>
          {step < 3 ? (
            <Button onClick={() => setStep(step + 1)}>
              Next
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitJob.isPending}
              variant="coral"
            >
              {submitJob.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                'Submit Job'
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Attorneys Tab Component
function AttorneysTab({
  selectedCountry,
  onSubmitAttorney,
}: {
  selectedCountry: string;
  onSubmitAttorney: () => void;
}) {
  const [specialtyFilter, setSpecialtyFilter] = useState('');

  const { data, isLoading } = useAttorneys({
    country: selectedCountry === 'ALL' ? undefined : selectedCountry,
    specialty: specialtyFilter || undefined,
    limit: 20,
  });

  const attorneys = data?.attorneys || [];

  return (
    <div className="space-y-6">
      {/* Pricing notice */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-4 border border-purple-200 dark:border-purple-800">
        <div className="flex items-start gap-3">
          <Scale className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-purple-900 dark:text-purple-100">Immigration Attorney Directory</h3>
            <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
              Connect with immigration attorneys who specialize in helping Koreans abroad.
              Listings start at <span className="font-semibold">$49/month</span>.
            </p>
          </div>
        </div>
      </div>

      {/* Header with filters and submit button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex-1">
          <select
            value={specialtyFilter}
            onChange={(e) => setSpecialtyFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm"
          >
            <option value="">All Specialties</option>
            {ATTORNEY_SPECIALTIES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <Button onClick={onSubmitAttorney} variant="coral" size="sm">
          <Plus className="w-4 h-4 mr-1" />
          List Your Practice
        </Button>
      </div>

      {/* Attorney listings */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : attorneys.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl p-8 text-center">
          <Scale className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No attorney listings yet
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Be the first immigration attorney to list your practice!
          </p>
          <Button onClick={onSubmitAttorney} variant="coral">
            <Plus className="w-4 h-4 mr-1" />
            List Your Practice
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {attorneys.map((attorney) => {
            const specialties = attorney.specialties_json ? JSON.parse(attorney.specialties_json) : [];
            const languages = attorney.languages_json ? JSON.parse(attorney.languages_json) : [];
            const countryData = COUNTRIES.find(c => c.code === attorney.country_code);

            return (
              <div
                key={attorney.id}
                className={`bg-white dark:bg-slate-800 rounded-xl p-4 border transition-colors ${
                  attorney.is_featured
                    ? 'border-purple-300 dark:border-purple-600 ring-1 ring-purple-200 dark:ring-purple-800'
                    : 'border-gray-200 dark:border-slate-700'
                }`}
              >
                {/* Featured/Verified badges */}
                {(attorney.is_featured || attorney.is_verified) && (
                  <div className="flex gap-1 mb-3">
                    {attorney.is_featured === 1 && (
                      <Badge variant="purple" size="sm">
                        <Star className="w-3 h-3 mr-1" />
                        Featured
                      </Badge>
                    )}
                    {attorney.is_verified === 1 && (
                      <Badge variant="green" size="sm">
                        <Award className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                  </div>
                )}

                <div className="flex items-start gap-3 mb-3">
                  {attorney.photo_url ? (
                    <img
                      src={attorney.photo_url}
                      alt={attorney.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                      <Scale className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 dark:text-white truncate">
                      {attorney.name}
                    </h4>
                    {attorney.firm_name && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {attorney.firm_name}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2 mb-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="truncate">
                      {countryData?.flag} {attorney.location}
                    </span>
                  </div>
                  {attorney.years_experience && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <Briefcase className="w-4 h-4 text-gray-400" />
                      <span>{attorney.years_experience}+ years experience</span>
                    </div>
                  )}
                  {languages.length > 0 && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <Languages className="w-4 h-4 text-gray-400" />
                      <span className="truncate">{languages.join(', ')}</span>
                    </div>
                  )}
                  {attorney.consultation_free === 1 ? (
                    <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                      <CheckCircle className="w-4 h-4" />
                      <span>Free consultation</span>
                    </div>
                  ) : attorney.consultation_fee && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <DollarSign className="w-4 h-4 text-gray-400" />
                      <span>Consultation: {attorney.consultation_fee}</span>
                    </div>
                  )}
                </div>

                {/* Specialties */}
                <div className="flex flex-wrap gap-1 mb-3">
                  {specialties.slice(0, 3).map((specialty: string, idx: number) => (
                    <Badge key={idx} variant="gray" size="sm">{specialty}</Badge>
                  ))}
                  {specialties.length > 3 && (
                    <Badge variant="gray" size="sm">+{specialties.length - 3}</Badge>
                  )}
                </div>

                {/* Contact buttons */}
                <div className="flex gap-2">
                  {attorney.email && (
                    <a
                      href={`mailto:${attorney.email}`}
                      className="flex-1 flex items-center justify-center gap-1 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      Contact
                    </a>
                  )}
                  {attorney.website && (
                    <a
                      href={attorney.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center px-3 py-2 border border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-lg text-sm transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Submit Attorney Modal Component
function SubmitAttorneyModal({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState(1);
  const [listingType, setListingType] = useState<'basic' | 'featured'>('basic');
  const [formData, setFormData] = useState<Partial<AttorneySubmissionInput>>({
    specialties: [],
    languages: ['English', 'Korean'],
  });
  const [error, setError] = useState('');

  const submitAttorney = useSubmitAttorney();

  const handleSubmit = async () => {
    setError('');

    if (!formData.name || !formData.email || !formData.location || !formData.country_code) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      await submitAttorney.mutateAsync({
        ...formData,
        listing_type: listingType,
      } as AttorneySubmissionInput);
      onClose();
      alert('Thank you! Your listing has been submitted for review. We will contact you about payment.');
    } catch (err: any) {
      setError(err.message || 'Failed to submit listing');
    }
  };

  const updateForm = (field: keyof AttorneySubmissionInput, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleSpecialty = (specialty: string) => {
    const current = formData.specialties || [];
    if (current.includes(specialty)) {
      updateForm('specialties', current.filter(s => s !== specialty));
    } else {
      updateForm('specialties', [...current, specialty]);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-slate-800 px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">List Your Practice</h3>
            <p className="text-sm text-gray-500">Step {step} of 3</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress */}
        <div className="px-6 py-3 bg-gray-50 dark:bg-slate-700/50">
          <div className="flex gap-2">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`flex-1 h-1.5 rounded-full ${
                  s <= step ? 'bg-purple-600' : 'bg-gray-200 dark:bg-slate-600'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg p-3 text-sm">
              {error}
            </div>
          )}

          {step === 1 && (
            <>
              <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-purple-500" />
                Choose Your Plan
              </h4>
              <div className="grid gap-3">
                <button
                  type="button"
                  onClick={() => setListingType('basic')}
                  className={`p-4 rounded-xl border-2 text-left transition-colors ${
                    listingType === 'basic'
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-gray-200 dark:border-slate-600'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900 dark:text-white">Basic Listing</span>
                    <span className="text-lg font-bold text-purple-600">$49/mo</span>
                  </div>
                  <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    <li>- Listed in Attorney directory</li>
                    <li>- Contact info displayed</li>
                    <li>- Specialty filters</li>
                  </ul>
                </button>
                <button
                  type="button"
                  onClick={() => setListingType('featured')}
                  className={`p-4 rounded-xl border-2 text-left transition-colors ${
                    listingType === 'featured'
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-gray-200 dark:border-slate-600'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900 dark:text-white">Featured Listing</span>
                      <Badge variant="purple" size="sm">Popular</Badge>
                    </div>
                    <span className="text-lg font-bold text-purple-600">$99/mo</span>
                  </div>
                  <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    <li>- Everything in Basic</li>
                    <li>- Featured badge & priority placement</li>
                    <li>- Highlighted listing</li>
                    <li>- Verified badge eligible</li>
                  </ul>
                </button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                <Scale className="w-5 h-5 text-purple-500" />
                Attorney Information
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={(e) => updateForm('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                    placeholder="John Kim, Esq."
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Firm Name
                  </label>
                  <input
                    type="text"
                    value={formData.firm_name || ''}
                    onChange={(e) => updateForm('firm_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                    placeholder="Kim Immigration Law"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Location *
                  </label>
                  <input
                    type="text"
                    value={formData.location || ''}
                    onChange={(e) => updateForm('location', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                    placeholder="Los Angeles, CA"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Country *
                  </label>
                  <select
                    value={formData.country_code || ''}
                    onChange={(e) => updateForm('country_code', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select country</option>
                    {COUNTRIES.filter(c => c.code !== 'ALL').map((c) => (
                      <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Years of Experience
                </label>
                <input
                  type="number"
                  value={formData.years_experience || ''}
                  onChange={(e) => updateForm('years_experience', parseInt(e.target.value) || undefined)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                  placeholder="10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Specialties (select all that apply)
                </label>
                <div className="flex flex-wrap gap-2">
                  {ATTORNEY_SPECIALTIES.map((specialty) => (
                    <button
                      key={specialty}
                      type="button"
                      onClick={() => toggleSpecialty(specialty)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        formData.specialties?.includes(specialty)
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {specialty}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-purple-500" />
                Contact & Consultation
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => updateForm('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                    placeholder="attorney@lawfirm.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone || ''}
                    onChange={(e) => updateForm('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Website
                </label>
                <input
                  type="url"
                  value={formData.website || ''}
                  onChange={(e) => updateForm('website', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                  placeholder="https://lawfirm.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Photo URL
                </label>
                <input
                  type="url"
                  value={formData.photo_url || ''}
                  onChange={(e) => updateForm('photo_url', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                  placeholder="https://example.com/photo.jpg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Consultation
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.consultation_free || false}
                      onChange={(e) => updateForm('consultation_free', e.target.checked)}
                      className="w-4 h-4 text-purple-600 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Free consultation</span>
                  </label>
                </div>
                {!formData.consultation_free && (
                  <input
                    type="text"
                    value={formData.consultation_fee || ''}
                    onChange={(e) => updateForm('consultation_fee', e.target.value)}
                    className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                    placeholder="$150/hour"
                  />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Bio / Description
                </label>
                <textarea
                  value={formData.bio || ''}
                  onChange={(e) => updateForm('bio', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                  placeholder="Tell potential clients about your experience and approach..."
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white dark:bg-slate-800 px-6 py-4 border-t border-gray-200 dark:border-slate-700 flex justify-between">
          <Button
            variant="outline"
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
          >
            {step > 1 ? 'Back' : 'Cancel'}
          </Button>
          {step < 3 ? (
            <Button onClick={() => setStep(step + 1)} className="bg-purple-600 hover:bg-purple-700">
              Next
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitAttorney.isPending}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {submitAttorney.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                `Submit (${listingType === 'featured' ? '$99' : '$49'}/mo)`
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
